import React from 'react'
import moment from 'moment'
import { useIntl } from 'gatsby-plugin-intl'
import styled from 'styled-components'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import { CloseIcon } from 'src/features/stocks/components/OfferDetails/atoms'
import ContainerHistoryIcon from 'src/assets/icons/container.svg'
import ShipHistoryIcon from 'src/assets/icons/ship.svg'
import CompanyHistoryIcon from 'src/assets/icons/work-case.svg'
import FavoriteOfferButton from './FavoriteOfferButton'

const getHistoryDateLabel = value =>
  value && moment(value).isValid() ? moment(value).format('DD.MM') : value

const getViewedAtLabel = value =>
  value && moment(value).isValid() ? moment(value).format('HH:mm') : ''

const OfferHistoryDrawer = ({
  history = [],
  mode = 'history',
  offerDetails,
  onBack,
  onClose,
  onFavoriteRemove,
  onOfferSelect
}) => {
  const intl = useIntl()
  const isFavoritesView = mode === 'favorites'
  const availableHistory = isFavoritesView
    ? history
    : history.filter(
        historyOffer =>
          historyOffer.id !== offerDetails?.id ||
          historyOffer.offerType !== offerDetails?.offerType
      )

  return (
    <Wrapper>
      <Header>
        <BackButton type='button' onClick={onBack}>
          <ArrowBackIcon fontSize='small' />
          <span>
            {intl.formatMessage({
              id: isFavoritesView ? 'offers.favorites' : 'offers.history'
            })}
          </span>
        </BackButton>
        <CloseIcon onClick={onClose} />
      </Header>
      <SectionTitle>
        {intl.formatMessage({
          id: isFavoritesView
            ? 'offers.favoriteOffers'
            : 'offers.recentlyViewed'
        })}
      </SectionTitle>
      <HistoryList>
        {isFavoritesView && availableHistory.length === 0 && (
          <EmptyList>
            {intl.formatMessage({ id: 'offers.noFavorites' })}
          </EmptyList>
        )}
        {availableHistory.map(historyOffer => (
          <HistoryItem key={`${historyOffer.offerType}:${historyOffer.id}`}>
            <HistoryItemButton
              type='button'
              hasTrailingAction={isFavoritesView}
              onClick={() => onOfferSelect(historyOffer)}
            >
              <HistoryCardHeader>
                <HistoryOfferReference>
                  {historyOffer.referenceNumber}
                </HistoryOfferReference>
                {!isFavoritesView && getViewedAtLabel(historyOffer.viewedAt) && (
                  <ViewedAt>
                    <AccessTimeIcon />
                    <span>{getViewedAtLabel(historyOffer.viewedAt)}</span>
                  </ViewedAt>
                )}
              </HistoryCardHeader>
              {(historyOffer.from || historyOffer.to) && (
                <HistoryOfferRoute>
                  {historyOffer.from && (
                    <HistoryOfferEndpoint>
                      <HistoryLocationName>
                        {historyOffer.from}
                      </HistoryLocationName>
                      {historyOffer.fromDate && (
                        <HistoryOfferDate>
                          {getHistoryDateLabel(historyOffer.fromDate)}
                        </HistoryOfferDate>
                      )}
                    </HistoryOfferEndpoint>
                  )}
                  {historyOffer.from && historyOffer.to && (
                    <HistoryOfferArrow>→</HistoryOfferArrow>
                  )}
                  {historyOffer.to && (
                    <HistoryOfferEndpoint>
                      <HistoryLocationName>
                        {historyOffer.to}
                      </HistoryLocationName>
                      {historyOffer.toDate && (
                        <HistoryOfferDate>
                          {getHistoryDateLabel(historyOffer.toDate)}
                        </HistoryOfferDate>
                      )}
                    </HistoryOfferEndpoint>
                  )}
                </HistoryOfferRoute>
              )}
              {(historyOffer.container ||
                historyOffer.shipowner ||
                historyOffer.company) && (
                <HistoryOfferProperties>
                  {historyOffer.container && (
                    <HistoryOfferProperty>
                      <ContainerHistoryIcon />
                      <span>{historyOffer.container}</span>
                    </HistoryOfferProperty>
                  )}
                  {historyOffer.shipowner && (
                    <HistoryOfferProperty>
                      <ShipHistoryIcon />
                      <span>{historyOffer.shipowner}</span>
                    </HistoryOfferProperty>
                  )}
                  {historyOffer.company && (
                    <HistoryOfferProperty>
                      <CompanyHistoryIcon />
                      <span>{historyOffer.company}</span>
                    </HistoryOfferProperty>
                  )}
                </HistoryOfferProperties>
              )}
            </HistoryItemButton>
            {isFavoritesView && (
              <FavoriteRemoveButton
                active
                onClick={() =>
                  onFavoriteRemove(historyOffer.offerType, historyOffer.id)
                }
              />
            )}
          </HistoryItem>
        ))}
      </HistoryList>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem 0.8rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.5).hex()};
`

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 700;
`

const SectionTitle = styled.div`
  padding: 0.8rem 1rem 0.65rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.colors.ghost.alpha(0.35).hex()};
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.7rem;
  font-weight: 700;
`

const HistoryList = styled.div`
  overflow-y: auto;
  padding: 0 1rem;
`

const HistoryItem = styled.div`
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.ghost.alpha(0.5).hex()};
`

const HistoryItemButton = styled.button`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: stretch;
  gap: 0.4rem;
  padding: ${({ hasTrailingAction }) =>
    hasTrailingAction ? '0.8rem 2rem 0.8rem 0' : '0.8rem 0'};
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  cursor: pointer;
  font-size: 0.72rem;
  text-align: left;
`

const FavoriteRemoveButton = styled(FavoriteOfferButton)`
  position: absolute;
  top: 0.6rem;
  right: 0;
`

const EmptyList = styled.div`
  padding: 1rem 0;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.72rem;
`

const HistoryCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const HistoryOfferReference = styled.span`
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  font-size: 0.78rem;
  font-weight: 700;
`

const ViewedAt = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.18rem;
  color: ${({ theme }) => theme.colors.corduroy.alpha(0.65).hex()};
  font-size: 0.62rem;
  font-weight: 400;
  svg {
    width: 13px;
    height: 13px;
  }
`

const HistoryOfferRoute = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
`

const HistoryOfferEndpoint = styled.span`
  display: flex;
  min-width: 0;
  align-items: baseline;
  gap: 0.3rem;
  &:last-child {
    flex: 1 1 auto;
  }
`

const HistoryLocationName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const HistoryOfferDate = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.63rem;
  font-weight: 400;
  white-space: nowrap;
`

const HistoryOfferArrow = styled.span`
  color: ${({ theme }) => theme.colors.apple.hex()};
  font-size: 1.05rem;
  font-weight: 700;
`

const HistoryOfferProperties = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem 0.65rem;
  color: ${({ theme }) => theme.colors.corduroy.hex()};
  font-size: 0.62rem;
`

const HistoryOfferProperty = styled.span`
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 0.25rem;
  svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    opacity: 0.72;
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

export default OfferHistoryDrawer
